// src/services/companyProfileService.ts
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  getDoc,
  getDocs,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../Firebase/Config';
import { getAuth } from 'firebase/auth';

const COMPANY_PROFILE_COLLECTION = 'companyProfiles';

export interface Address {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

export interface ContactInfo {
  phone: string;
  email: string;
  website: string;
  fax: string;
}

export interface CompanyProfile {
  id?: string;
  companyName: string;
  businessType: string;
  registrationNumber: string;
  taxId: string;
  address: Address;
  contactInfo: ContactInfo;
  logo: string;
  currency: string;
  currencySymbol?: string;
  timezone: string;
  fiscalYearStart: string;
  notes: string;
  displayPrices: boolean;
  createdAt?: Timestamp;
  createdBy?: string;
  updatedAt?: Timestamp;
  updatedBy?: string;
}

// Currency symbols mapping
const CURRENCY_SYMBOLS: { [key: string]: string } = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  AUD: 'A$',
  CAD: 'C$',
  CHF: 'CHF',
  CNY: '¥',
  INR: '₹',
  LKR: 'Rs',
  NZD: 'NZ$',
  SGD: 'S$',
  HKD: 'HK$',
  KRW: '₩',
  MXN: 'MX$',
  BRL: 'R$',
  ZAR: 'R',
  RUB: '₽',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  PLN: 'zł',
  THB: '฿',
  IDR: 'Rp',
  MYR: 'RM',
  PHP: '₱',
  AED: 'د.إ',
  SAR: '﷼',
  TRY: '₺',
  EGP: 'E£'
};

/**
 * Get currency symbol from currency code
 */
export const getCurrencySymbol = (currencyCode: string): string => {
  return CURRENCY_SYMBOLS[currencyCode] || currencyCode;
};

/**
 * Format currency with proper symbol and decimal places
 */
export const formatCurrency = (
  amount: number,
  currencySymbol: string = '$',
  currencyCode: string = 'USD'
): string => {
  const decimals = ['JPY', 'KRW'].includes(currencyCode) ? 0 : 2;
  const formattedAmount = amount.toFixed(decimals);
  
  // For some currencies, symbol comes after the amount
  const symbolAfterCurrencies = ['SEK', 'NOK', 'DKK', 'PLN'];
  
  if (symbolAfterCurrencies.includes(currencyCode)) {
    return `${formattedAmount} ${currencySymbol}`;
  }
  
  return `${currencySymbol}${formattedAmount}`;
};

/**
 * Get default company profile (fallback)
 */
export const getDefaultCompanyProfile = (): CompanyProfile => {
  return {
    companyName: 'Your Company',
    businessType: 'Business',
    registrationNumber: '',
    taxId: '',
    address: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: ''
    },
    contactInfo: {
      phone: '',
      email: '',
      website: '',
      fax: ''
    },
    logo: '',
    currency: 'USD',
    currencySymbol: '$',
    timezone: 'UTC',
    fiscalYearStart: '01-01',
    notes: '',
    displayPrices: true
  };
};

/**
 * Fetch company profile from Firestore
 * For simplicity, we assume one company profile per account
 */
export const fetchCompanyProfile = async (): Promise<CompanyProfile | null> => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      console.warn('No user logged in');
      return null;
    }

    // Get all company profiles (in this implementation, we expect only one)
    const companiesRef = collection(db, COMPANY_PROFILE_COLLECTION);
    const snapshot = await getDocs(companiesRef);
    
    if (snapshot.empty) {
      console.warn('No company profile found');
      return null;
    }

    // Get the first (and presumably only) company profile
    const docSnap = snapshot.docs[0];
    const data = docSnap.data();

    // Get currency symbol
    const currencySymbol = getCurrencySymbol(data.currency || 'USD');

    const profile: CompanyProfile = {
      id: docSnap.id,
      companyName: data.companyName || '',
      businessType: data.businessType || '',
      registrationNumber: data.registrationNumber || '',
      taxId: data.taxId || '',
      address: {
        street: data.address?.street || '',
        city: data.address?.city || '',
        state: data.address?.state || '',
        postalCode: data.address?.postalCode || '',
        country: data.address?.country || ''
      },
      contactInfo: {
        phone: data.contactInfo?.phone || '',
        email: data.contactInfo?.email || '',
        website: data.contactInfo?.website || '',
        fax: data.contactInfo?.fax || ''
      },
      logo: data.logo || '',
      currency: data.currency || 'USD',
      currencySymbol: currencySymbol,
      timezone: data.timezone || 'UTC',
      fiscalYearStart: data.fiscalYearStart || '01-01',
      notes: data.notes || '',
      displayPrices: data.displayPrices !== false,
      createdAt: data.createdAt,
      createdBy: data.createdBy || '',
      updatedAt: data.updatedAt,
      updatedBy: data.updatedBy || ''
    };

    return profile;
  } catch (error: any) {
    console.error('Error fetching company profile:', error);
    if (error.code === 'permission-denied') {
      throw new Error('You do not have permission to access company data.');
    }
    throw error;
  }
};

/**
 * Get company profile by ID
 */
export const getCompanyProfileById = async (companyId: string): Promise<CompanyProfile | null> => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to view company profile');
    }

    const companyRef = doc(db, COMPANY_PROFILE_COLLECTION, companyId);
    const companySnap = await getDoc(companyRef);
    
    if (!companySnap.exists()) {
      return null;
    }

    const data = companySnap.data();
    const currencySymbol = getCurrencySymbol(data.currency || 'USD');
    
    return {
      id: companySnap.id,
      companyName: data.companyName || '',
      businessType: data.businessType || '',
      registrationNumber: data.registrationNumber || '',
      taxId: data.taxId || '',
      address: {
        street: data.address?.street || '',
        city: data.address?.city || '',
        state: data.address?.state || '',
        postalCode: data.address?.postalCode || '',
        country: data.address?.country || ''
      },
      contactInfo: {
        phone: data.contactInfo?.phone || '',
        email: data.contactInfo?.email || '',
        website: data.contactInfo?.website || '',
        fax: data.contactInfo?.fax || ''
      },
      logo: data.logo || '',
      currency: data.currency || 'USD',
      currencySymbol: currencySymbol,
      timezone: data.timezone || 'UTC',
      fiscalYearStart: data.fiscalYearStart || '01-01',
      notes: data.notes || '',
      displayPrices: data.displayPrices !== false,
      createdAt: data.createdAt,
      createdBy: data.createdBy || '',
      updatedAt: data.updatedAt,
      updatedBy: data.updatedBy || ''
    };
  } catch (error: any) {
    console.error('Error fetching company profile:', error);
    if (error.code === 'permission-denied') {
      throw new Error('You do not have permission to access company data.');
    }
    throw error;
  }
};

/**
 * Create a new company profile
 */
export const createCompanyProfile = async (profile: CompanyProfile): Promise<string> => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to create a company profile');
    }

    // Check if a company profile already exists
    const existingProfile = await fetchCompanyProfile();
    if (existingProfile) {
      throw new Error('A company profile already exists. Please update the existing profile instead.');
    }

    // Create new company profile document
    const companyRef = doc(collection(db, COMPANY_PROFILE_COLLECTION));
    
    await setDoc(companyRef, {
      companyName: profile.companyName,
      businessType: profile.businessType || '',
      registrationNumber: profile.registrationNumber || '',
      taxId: profile.taxId || '',
      address: profile.address,
      contactInfo: profile.contactInfo,
      currency: profile.currency || 'USD',
      timezone: profile.timezone || 'UTC',
      fiscalYearStart: profile.fiscalYearStart || '01-01',
      logo: profile.logo || '',
      notes: profile.notes || '',
      displayPrices: profile.displayPrices !== false,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy: currentUser.uid,
      updatedBy: currentUser.uid,
    });

    return companyRef.id;
  } catch (error: any) {
    console.error('Error creating company profile:', error);
    if (error.code === 'permission-denied') {
      throw new Error('You do not have permission to create company data.');
    }
    throw error;
  }
};

/**
 * Update existing company profile
 */
export const updateCompanyProfile = async (profile: CompanyProfile): Promise<void> => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to update company profile');
    }

    // Get the existing company profile to find its ID
    const existingProfile = await fetchCompanyProfile();
    if (!existingProfile || !existingProfile.id) {
      throw new Error('No company profile found to update');
    }

    const companyRef = doc(db, COMPANY_PROFILE_COLLECTION, existingProfile.id);
    
    await updateDoc(companyRef, {
      companyName: profile.companyName,
      businessType: profile.businessType || '',
      registrationNumber: profile.registrationNumber || '',
      taxId: profile.taxId || '',
      address: profile.address,
      contactInfo: profile.contactInfo,
      currency: profile.currency || 'USD',
      timezone: profile.timezone || 'UTC',
      fiscalYearStart: profile.fiscalYearStart || '01-01',
      logo: profile.logo || '',
      notes: profile.notes || '',
      displayPrices: profile.displayPrices !== false,
      updatedAt: serverTimestamp(),
      updatedBy: currentUser.uid,
    });
  } catch (error: any) {
    console.error('Error updating company profile:', error);
    if (error.code === 'permission-denied') {
      throw new Error('You do not have permission to update company data.');
    }
    throw error;
  }
};

/**
 * Update company logo
 */
export const updateCompanyLogo = async (logoUrl: string): Promise<void> => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to update company logo');
    }

    const existingProfile = await fetchCompanyProfile();
    if (!existingProfile || !existingProfile.id) {
      throw new Error('No company profile found to update');
    }

    const companyRef = doc(db, COMPANY_PROFILE_COLLECTION, existingProfile.id);
    
    await updateDoc(companyRef, {
      logo: logoUrl,
      updatedAt: serverTimestamp(),
      updatedBy: currentUser.uid,
    });
  } catch (error: any) {
    console.error('Error updating logo:', error);
    if (error.code === 'permission-denied') {
      throw new Error('You do not have permission to update company data.');
    }
    throw error;
  }
};

/**
 * Delete company profile
 * Note: Use with caution - this will permanently delete the company profile
 */
export const deleteCompanyProfile = async (): Promise<void> => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to delete company profile');
    }

    const existingProfile = await fetchCompanyProfile();
    if (!existingProfile || !existingProfile.id) {
      throw new Error('No company profile found to delete');
    }

    const companyRef = doc(db, COMPANY_PROFILE_COLLECTION, existingProfile.id);
    
    // Archive instead of delete
    await updateDoc(companyRef, {
      isActive: false,
      updatedAt: serverTimestamp(),
      updatedBy: currentUser.uid,
    } as any);
  } catch (error: any) {
    console.error('Error deleting company profile:', error);
    if (error.code === 'permission-denied') {
      throw new Error('You do not have permission to delete company data.');
    }
    throw error;
  }
};

/**
 * Get formatted company address
 */
export const getFormattedAddress = (address: Address): string => {
  const parts = [
    address.street,
    address.city,
    address.state,
    address.postalCode,
    address.country
  ].filter(Boolean);

  return parts.join(', ');
};

/**
 * Validate email format
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone format (basic)
 */
export const isValidPhone = (phone: string): boolean => {
  const phoneRegex = /^[\d\s\-\+\(\)]+$/;
  return phone.length >= 7 && phoneRegex.test(phone);
};

/**
 * Validate fiscal year start format (MM-DD)
 */
export const validateFiscalYearStart = (fiscalYearStart: string): boolean => {
  const regex = /^(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/;
  return regex.test(fiscalYearStart);
};

/**
 * Get company display name (for use in headers, reports, etc.)
 */
export const getCompanyDisplayName = async (): Promise<string> => {
  try {
    const profile = await fetchCompanyProfile();
    return profile?.companyName || 'Company';
  } catch (error) {
    console.error('Error getting company display name:', error);
    return 'Company';
  }
};